/**
 * Reading "which course is this screen about?" out of a URL, in ONE place.
 *
 * The application spells that question **two different ways**, and the two halves of the app do not
 * read each other's spelling:
 *
 * - `CourseBrowser` reads and writes **`?courseId`** (`CourseBrowser.tsx`, and
 *   {@link buildCourseBrowserPath} in `authorNavigation.ts`).
 * - `AppShell`, the work queue, the board, structural review and the release-waves dashboard read
 *   **`?courseKey`**.
 *
 * The consequence was live and silent: standing on `/courses?courseId=10b`, the shell asked for
 * `courseKey`, found nothing, and fell through `selectAuthoringCourseKey` to the hard-coded
 * preference — so every tab in the header (Work Queue, Board, Structural, Waves) pointed at course
 * `12b` while the page underneath was showing `10b`, and the "Structural" pending badge counted
 * `12b`'s items. Nothing on screen said so, because the shell named no course at all. Lesson ids are
 * per-course and collide freely (`CourseBrowser.tsx` `parsePastedLessonUrl`), so "the same lesson
 * number in the other course" is a different lesson.
 *
 * `courseKey` is the CANONICAL spelling — it is what the queue, board, waves and structural screens
 * use, and it is what the reviewer's Slack deep links carry (`queue.service.ts` builds
 * `${origin}/queue?courseKey=…`). `courseId` is accepted as a **legacy alias on read only**, so
 * every `?courseId=` URL already bookmarked or already pasted into Slack keeps working. Nothing here
 * writes the alias; converging on one written name is a separate, deliberate change.
 */

/** In precedence order: the canonical name first, the legacy alias second. */
export const COURSE_PARAM_NAMES = ['courseKey', 'courseId'] as const;

export interface CourseParamSource {
  get(name: string): string | null;
}

/**
 * The course named by a URL, under either spelling, or `null` when it names none.
 *
 * A present-but-empty parameter is `null`, not `''`: `?courseKey=` names no course, and returning
 * `''` would let a caller treat "the URL was explicit" and "the URL said nothing" as the same thing
 * while passing an empty course key downstream.
 */
export function readCourseParam(params: CourseParamSource): string | null {
  for (const name of COURSE_PARAM_NAMES) {
    const value = params.get(name);
    if (value != null && value.trim() !== '') {
      return value;
    }
  }

  return null;
}
